import { I18nThemed } from 'interfaces/mixins';
import { theme } from '@dojo/framework/core/mixins/Themed';
import { v, w } from '@dojo/framework/core/vdom';
import { beforeProperties } from '@dojo/framework/core/decorators/beforeProperties';
import { DNode, WidgetProperties } from '@dojo/framework/core/interfaces';
import { Messages } from '@dojo/framework/i18n/i18n';

import { lccstore } from 'lcc-state';
import { publish, subscribe, Handle } from 'pub-sub';

import Button from '@dojo/widgets/button';
import { Editor } from 'editor-widget';
import { displayDate } from '../support/display-date';

import { EssayData, CommentData } from '../interfaces';

import css from '../css/discussion.m.css';
import { IDiscussionRpc } from '../support/discussion-svc';

export interface CommentProperties {
	comment: CommentData;
}

@theme(css)
export class Comment extends I18nThemed<CommentProperties> {
	protected render(): DNode {
		const {
			comment: { fullname, posted, comment }
		} = this.properties;

		return v('div', { key: posted, classes: [this.theme(css.comment)] }, [
			v('div', { key: 'user', classes: [this.theme(css.commentUser)] }, [fullname]),
			v('div', { key: 'date', classes: [this.theme(css.commentDate)] }, [displayDate(posted)]),
			v('div', { key: 'comment', classes: [this.theme(css.commentText)], innerHTML: comment })
		]);
	}
}

export interface CommentEditorProperties {
	resultid: string;
	svc: IDiscussionRpc;
	reloadEssayPane: () => void;
	localizedBundle: { isPlaceholder: boolean; format: any; messages: Messages };
}

@theme(css)
export class CommentEditor extends I18nThemed<CommentEditorProperties> {
	private data: string = '';
	private setData: (data: string) => void = () => undefined;
	private active: boolean = false;

	protected onAttach(): void {
		//
	}

	protected update_comment(data: string): void {
		this.data = data;
	}

	@beforeProperties()
	ensure_nls(properties: CommentEditorProperties & WidgetProperties): CommentEditorProperties & WidgetProperties {
		const { get, path } = lccstore;
		const dynls = get(path('dynls'));
		const bundle = dynls.get('quiz');
		if (!bundle) {
			properties.localizedBundle = { isPlaceholder: true, format: null, messages: {} as Messages };
		} else {
			properties.localizedBundle = this.localizeBundle(bundle);
		}
		return properties;
	}

	protected async save_comment(): Promise<void> {
		if (this.data.length > 1) {
			const { svc, resultid, reloadEssayPane } = this.properties;
			const comment = await svc.add_reply({ resultid, reply: this.data });
			publish('notify:event', {
				event: 'discussion:comment:add',
				recipient: '*',
				course: (window.hcstatic.course_session! as any).pk,
				message: {
					resultid,
					commentid: comment.commentid,
					user: comment.fullname
				}
			});
			this.data = '';
			this.setData('');
			this.invalidate();
			reloadEssayPane();
		}
	}

	protected render_editor(): DNode {
		const {
			localizedBundle: {
				messages: { discPost = 'Post', discCancel = 'Cancel' }
			}
		} = this.properties;

		return v('div', { key: 'editor-view', xqa: `comment editor`, classes: [this.theme(css.commentEditor)] }, [
			w<typeof Editor>('Editor', {
				content: this.data,
				onContent: (content: string) => {
					this.update_comment(content);
				}
			}),
			w(
				Button,
				{
					onClick: () => {
						this.active = false;
						this.save_comment();
					}
				},
				[discPost]
			),
			w(
				Button,
				{
					onClick: () => {
						this.data = '';
						this.setData('');
						this.active = false;
						this.invalidate();
					}
				},
				[discCancel]
			)
		]);
	}

	protected render_inactive(): DNode {
		const {
			localizedBundle: {
				messages: { discAddComment = 'Add Comment' }
			}
		} = this.properties;

		return v('div', { key: 'editor-view', xqa: `comment add`, classes: [this.theme(css.commentEditor)] }, [
			w(
				Button,
				{
					onClick: () => {
						this.active = true;
						this.invalidate();
					}
				},
				[discAddComment]
			)
		]);
	}

	protected render(): DNode {
		const { active } = this;
		const {
			localizedBundle: { isPlaceholder }
		} = this.properties;
		if (isPlaceholder) {
			return null;
		}

		return active ? this.render_editor() : this.render_inactive();
	}
}

export interface EssayPaneProperties {
	essay: EssayData;
	comments: CommentData[];
	reloadComments: () => void;
	svc: IDiscussionRpc;
}

@theme(css)
export class EssayPane extends I18nThemed<EssayPaneProperties> {
	private handles: Handle[] = [];

	onAttach(): void {
		const {
			essay: { qrid },
			reloadComments
		} = this.properties;
		const h = subscribe('discussion:comment:add', (topic, data: { [key: string]: any }) => {
			if (data.resultid === qrid) {
				reloadComments();
			}
		});
		this.handles.push(h);
	}

	onDetach(): void {
		this.handles.forEach((h) => {
			h.destroy();
		});
	}

	protected render(): DNode {
		const {
			essay: { fullname, posted, answer, qrid },
			comments,
			reloadComments,
			svc
		} = this.properties;

		return v('div', { key: posted, classes: [this.theme(css.essayPane)] }, [
			v('div', { classes: [this.theme(css.essayUser)] }, [v('span', {}, [fullname])]),
			v('div', { classes: [this.theme(css.essayDate)] }, [v('span', {}, [displayDate(posted)])]),
			v('div', { classes: [this.theme(css.essayAnswer)], innerHTML: answer }),
			v('div', { xqa: `comment list`, classes: [this.theme(css.essayComments)] }, [
				...comments.map((comment) => {
					return w(Comment, { comment });
				}),
				w(CommentEditor, {
					resultid: `${qrid}`,
					svc,
					reloadEssayPane: () => {
						reloadComments();
					},
					localizedBundle: { isPlaceholder: true, format: null, messages: {} as Messages }
				})
			])
		]);
	}
}

export interface EssayItemProperties {
	essay: EssayData;
	key: string;
	isSelected: boolean;
	selectItem: (essay: EssayData) => void;
}

@theme(css)
export class EssayItem extends I18nThemed<EssayItemProperties> {
	protected render(): DNode {
		const {
			essay: { fullname, posted, excerpt },
			key,
			selectItem,
			isSelected
		} = this.properties;

		return v(
			'li',
			{
				key,
				classes: [this.theme(css.essayItem), isSelected ? this.theme(css.selectedEssayItem) : null],
				onclick: (evt) => {
					evt && evt.stopPropagation();
					selectItem(this.properties.essay);
				}
			},
			[
				v('div', { classes: [this.theme(css.essayUser)] }, [v('span', {}, [fullname])]),
				v('div', { classes: [this.theme(css.essayDate)] }, [v('span', {}, [displayDate(posted)])]),
				v('div', { classes: [this.theme(css.essayFragment)] }, [excerpt])
			]
		);
	}
}
